import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { SheetDragDismissDirective } from '../../directives/sheet-drag-dismiss.directive';
import { NEW_ORG_PART_QUICK_ACTION } from './new-org-part-switch';

/** Permission flags that decide which tools to show. */
export interface OrgBoardToolsSheetData {
    mayManageUsers: boolean;
    mayManageHats: boolean;
    mayManagePolicies: boolean;
    mayManageSecurityGroups: boolean;
    /**
     * Dark-deploy switchboard (design §3b, non-route surfaces) — whether the
     * `invitations`/`policies` module is on for this user/org. A permission flag
     * above says the user MAY do the thing; these say the module itself is even
     * switched on. Only the two module-tied "Quick Actions" key on them — Staff
     * browsing (an `organization` surface) stays governed by `mayManageUsers` alone.
     */
    invitationsModuleOn: boolean;
    policiesModuleOn: boolean;
    /** The sheet is the only way to flip the edit-only "Hide staff" toggle on phones (the
     *  desktop header row with the slide toggle is display:none there). */
    isEditing?: boolean;
    hideStaffOnBoard?: boolean;
}

/**
 * Mobile edit-mode tool palette, shown as a bottom sheet so the board stays
 * visible above it (the desktop renders these as a side panel). Picking an item
 * dismisses the sheet and returns the tool's value string, which maps 1:1 to the
 * board's ToolbarOptionsEnum — the board runs the same selection logic.
 */
@Component({
    selector: 'shared-org-board-tools-sheet',
    imports: [CommonModule, MatListModule, MatIconModule, MatDividerModule, SheetDragDismissDirective],
    template: `
        <div class="ng-sheet-grab" ngSheetDrag aria-hidden="true"></div>
        <mat-action-list class="ob-tools-sheet">
            <button mat-list-item *ngIf="data.isEditing" (click)="pick('toggle_hide_staff')">
                <mat-icon matListItemIcon>{{ data.hideStaffOnBoard ? 'visibility' : 'visibility_off' }}</mat-icon>
                <span matListItemTitle>{{ data.hideStaffOnBoard ? 'Show staff' : 'Hide staff' }}</span>
                <span matListItemLine>Work on the structure without the people on the posts.</span>
            </button>
            <mat-divider *ngIf="data.isEditing"></mat-divider>
            <button mat-list-item *ngIf="data.mayManageUsers" (click)="pick('users')">
                <mat-icon matListItemIcon>account_circle</mat-icon>
                <span matListItemTitle>Staff</span>
                <span matListItemLine>People within the organization.</span>
            </button>
            <button mat-list-item *ngIf="data.mayManageHats" (click)="pick('hats')">
                <mat-icon matListItemIcon>school</mat-icon>
                <span matListItemTitle>Hats</span>
                <span matListItemLine>Hats worn by people.</span>
            </button>
            <button mat-list-item *ngIf="data.mayManagePolicies && data.policiesModuleOn" (click)="pick('policies')">
                <mat-icon matListItemIcon>policy</mat-icon>
                <span matListItemTitle>Policies</span>
                <span matListItemLine>Policies of the Organization.</span>
            </button>
            <button mat-list-item *ngIf="data.mayManageSecurityGroups" (click)="pick('security_groups')">
                <mat-icon matListItemIcon>badge</mat-icon>
                <span matListItemTitle>Security Groups</span>
                <span matListItemLine>Authorization for each Hat/Staff Member.</span>
            </button>
            <button mat-list-item (click)="pick('positions')">
                <mat-icon matListItemIcon>business</mat-icon>
                <span matListItemTitle>Org Parts Scaffolding</span>
                <span matListItemLine>Organization hierarchy.</span>
            </button>

            <mat-divider></mat-divider>
            <div mat-subheader>Quick Actions</div>

            <button mat-list-item *ngIf="data.mayManageUsers && data.invitationsModuleOn" (click)="pick('new_user')">
                <mat-icon matListItemIcon>add</mat-icon>
                <span matListItemTitle>Invite Staff Member</span>
            </button>
            <button mat-list-item *ngIf="data.mayManagePolicies && data.policiesModuleOn" (click)="pick('new_policy')">
                <mat-icon matListItemIcon>add</mat-icon>
                <span matListItemTitle>New Policy</span>
            </button>
            <button mat-list-item *ngIf="data.mayManageHats" (click)="pick('new_hat')">
                <mat-icon matListItemIcon>add</mat-icon>
                <span matListItemTitle>New Hat</span>
            </button>
            <button mat-list-item *ngIf="newOrgPartOn" (click)="pick('new_part')">
                <mat-icon matListItemIcon>add</mat-icon>
                <span matListItemTitle>New Org Part</span>
            </button>
            <button mat-list-item *ngIf="data.mayManageSecurityGroups" (click)="pick('new_sg')">
                <mat-icon matListItemIcon>add</mat-icon>
                <span matListItemTitle>New Security Group</span>
            </button>
        </mat-action-list>
    `,
    // System-bar inset comes from the GLOBAL container rule
    // (global-styles.scss, DESIGN.md §Sheets) — no self-padding.
    styles: [],
})
export class OrgBoardToolsSheetComponent {
    public readonly newOrgPartOn = NEW_ORG_PART_QUICK_ACTION;

    constructor(
        private ref: MatBottomSheetRef<OrgBoardToolsSheetComponent, string>,
        @Inject(MAT_BOTTOM_SHEET_DATA) public data: OrgBoardToolsSheetData
    ) {}

    public pick(value: string): void {
        this.ref.dismiss(value);
    }
}
